
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { VueBootstrapField } from "@/store/types";
import { validationMixin } from "vuelidate";
import BulkImport, {
	LoadedFocusedComponent,
	BulkCallbackResult,
	BulkImportDataTypes,
	csvTableData,
	BulkType,
	ValidationStatus,
	BulkImportOptionStatus,
	BulkImportOption
} from "@/bulk-import/BulkImport";
import BulkImportValidationIcon from "@/components/bulk-import/BulkImportValidationIcon.vue";

import { required, requiredIf, minValue, maxValue, numeric } from "vuelidate/lib/validators";
import { metadata } from "@/store/metadata";
import { AlarmBulkType } from "@/bulk-import/AlarmBulkImport";
import SureViewIcon from "../SureViewIcon.vue";
import { namespace } from "vuex-class";

@Component({
	mixins: [validationMixin],
	validations() {
		const properties: Map<string, any> = new Map(
			this.metaData.map((x: BulkImport) => [x.displayProperty, x.validation])
		);

		let validation = Object.fromEntries(properties);

		// Allow binding of this, which allows us to access
		// the vm instance in our validation if required.
		for (const [key, value] of Object.entries(validation)) {
			if (typeof value == "function") {
				value[key] = value[key]().bind(this);
			}
		}

		return {
			data: {
				$each: {
					...validation
				}
			}
		};
	},
	directives: {
		// ***** Credit: javiercbk - https://forum.vuejs.org/t/dynamic-props-and-custom-event-emit-in-dynamic-component/1093
		DynamicEvents: {
			bind: function(el, binding, vnode) {
				const allEvents = binding.value;
				allEvents.forEach(event => {
					// register handler in the dynamic component
					vnode.componentInstance.$on(event, (eventData, loading) => {
						// when the event is fired, the proxyEvent function is going to be called
						vnode.context.proxyEvent(event, eventData, loading);
					});
				});
			},
			unbind: function(el, binding, vnode) {
				vnode.componentInstance.$off();
			}
		}
	},
	components: {
		"sureview-icon": SureViewIcon,
		"bulkimport-validation-icon": BulkImportValidationIcon
	}
})
export default class BulkImportTable extends Vue {
	// -- Props/Models -- //

	/**
	 * @summary metaData for each of the columns of the table
	 */
	@Prop({ default: () => [], type: Array }) metaData: BulkImport[];

	/**
	 * @summary Prop which contains the loaded csv data which is now in json format.
	 */
	@Prop({ default: () => [], type: Array }) csvData: any[];

	/**
	 * @summary Prop which enables/disable visible state for actions
	 */
	@Prop({ default: true, type: Boolean }) showActions: boolean;

	/**
	 * @summary Prop which contains any server side validation failures
	 */
	@Prop({ default: () => ({}), type: Object }) serverSideValidation: Object;

	/**
	 * @summary Prop which contains the count of submitted attempts
	 */
	@Prop({ default: 0, type: Number }) submissionAttempts: number;

	/**
	 * @summary Prop which contains the id's of rows which has been submitted.
	 */
	@Prop({ default: () => [], type: Array }) submittedIds: number[];

	/**
	 * @summary Prop which maps the parsedData to the desired data which will be sent
	 * to the backend for validation and saving.
	 */
	@Prop({ default: null, type: Function }) mapValues: (csvData: csvTableData<any>[], rawData: any[]) => any[];

	@Prop({ default: null, type: Object }) bulkType: BulkType<any, any> | null;

	// -- Properties -- //

	/**
	 * @summary csv data which can be edited within the table.
	 */
	private data: any[] = [];

	/**
	 * @summary search term which allows searching of all the columns/fields in the table
	 */
	private searchTerm: string = "";

	private resolveMultipleModal: boolean = false;

	private showInvalidRows: boolean = false;

	private valuesMapped: boolean = false;

	private index: number = 0;

	private bulkImportReady: boolean = false;

	/**
	 * @summary
	 * RawData is mirrored array of the data object, however contains the value properties and data.
	 * This allows little computing when saving data.
	 */
	private rawData: any[] = [];

	private bulkImportDataTypes: typeof BulkImportDataTypes = BulkImportDataTypes;

	private validationStatus: typeof ValidationStatus = ValidationStatus;

	private focusedComponetTemplate: LoadedFocusedComponent = {
		id: -1,
		property: "",
		component: null,
		index: -1,
		props: {},
		events: {},
		hasVModel: false
	};
	/**
	 * @summary current focused component, used to render the desired component and pass the desired props/events down.
	 */
	private focusedComponent: LoadedFocusedComponent = { ...this.focusedComponetTemplate };

	private currentPage: number = 1;

	private pageSize: number = 15;

	private previousEdited: { index: number; property: string } = { index: 0, property: "" };

	private previousValue: string | number | any | null = null;

	private newValue: string | number | any | null = null;

	private totalRows: number = 0;

	private tableIsBusy: boolean = false;

    private currentRow: any | null = null;

	// -- Life Cycle Hooks -- //

	/**
	 * @summary Mounted hook, this loads the table items.
	 */
	private async mounted(): Promise<void> {
		this.tableIsBusy = true;
		// First Parse data
		this.tableItems(this.csvData);

		// Then request all mounted functions
		for (let index = 0; index < this.metaData.length; index++) {
			if (this.metaData[index]?.mounted) {
				await this.metaData[index]?.mounted([...this.data], this.rawData);
			}
		}

		this.tableIsBusy = false;

		// Then map data.
		await this.mapValueData();

		this.bulkImportReady = true;
	}

	// -- Computed Properties -- //

	/**
	 * @summary Computed property which returns the cuttent table data
	 */
	private get tableData(): any[] {
		return this.showInvalidRows
			? this.data.filter(
					(val, index) => this.$v.data.$each[index].$invalid || (this.serverSideValidation[val.id] ?? false)
			  )
			: this.data ?? [];
	}

	/**
	 * @summary Computed property which maps the metaData to VueBootstrapFields for the btable
	 */
	private get tableFields(): VueBootstrapField[] {
		const fields = this.metaData.map(x => ({
			key: x.displayProperty,
			label: x.header,
			dataType: x.dataType.toLowerCase(),
			class: `mw-100 ${x.rowClass ?? ""}`,
			thClass: x.thClass ?? ""
		})) as VueBootstrapField[];

		fields.unshift({ key: "valid", label: "", class: "w-h-25" });
		fields.unshift({ key: "id", label: "", class: "w-h-25" });

		if (this.showActions) {
			fields.push({ key: "actions", label: "Actions", thClass: "text-center" });
		}

		return fields;
	}

	/**
	 * @summary Computed property which returns the events names.
	 */
	private get getEvents(): string[] {
		if (!this.focusedComponent.events) return [];
		return Object.entries(this.focusedComponent.events).map(x => x[0]);
	}

	/**
	 * @summary Computed property which will return the current table count.
	 */
	private get rowCount(): number {
		if (this.searchTerm) {
			return this.totalRows;
		}
		return this.tableData.length;
	}

	/**
	 * @summary Computed property which will return the invalid rows count.
	 */
	private get invalidCount(): number {
		const invalid = Object.entries(this.$v?.data?.$each?.$iter).filter(x => x[1]?.$invalid == true)?.length ?? 0;
		if (invalid) return invalid;
		return Object.keys(this.serverSideValidation).length;
	}

	/**
	 * @summary Getter for vmodel for focused component
	 */
	private get vmodel(): any {
		if (!this.focusedComponent?.hasVModel) {
			return null;
		}

		const row = this.data.find(x => x.id == this.focusedComponent.id);
		return row[this.focusedComponent.property];
	}

	/**
	 * @summary setter for vmodel for focused component
	 * @summary if the focusedComponent hasVmodel is false, it will not set the content.
	 */
	private set vmodel(vmodel: number | string | any) {
		if (!this.focusedComponent?.hasVModel) return;
		const row = this.data.find(x => x.id == this.focusedComponent.id);
		row[this.focusedComponent.property] = vmodel;
	}

	// -- Watchers -- //

	/**
	 * @summary Watcher which Emits our data to the modal when the full table is valid.
	 */
	@Watch("$v.data.$invalid", { deep: true })
	private onValidTable(invalid: boolean, prevInvalid: boolean): void {
		if (invalid && prevInvalid) return;

		if (!this.valuesMapped) {
			setTimeout(() => this.onValidTable(invalid, prevInvalid), 50);
			return;
		}

		this.$emit("invalid", invalid);

		// Check if we got any validation,
		// if not ensure we aren't stuck in show invalid
		if (this.invalidCount === 0) {
			this.showInvalidRows = false;
		}

		this.$emit("data", this.rawData);
	}

	// -- Methods -- //

	/**
	 * @summary Method which sets the tableItems if the csvData prop is updated.
	 */
	private tableItems(data: any[]): void {
		if (!data) return;

		// INFO: we map and spread to make sure our array of
		// objects are all passed by value, not reference.
		this.data = this.csvData.map((x, index) => ({
			...x,
			id: this.incrementIndex()
		}));
		this.$v.$reset();
		this.$v.$touch();
	}

	private incrementIndex() {
		this.index++;
		return this.index;
	}

	/**
	 * @summary Method which resets the focused component,
	 * this is done on unfocus of the current focused component
	 */
	private componentUnfocused(): void {
		this.focusedComponent = { ...this.focusedComponetTemplate };
	}

	/**
	 * @summary Method which will map the bulk import data to the desired
	 * submission data (actual data)
	 * We do this as the actual value may not be set,
	 * but display value will be.
	 */
	private async mapValueData(): Promise<void> {
		if (!this.mapValues) return;

		this.valuesMapped = false;

		this.rawData = await this.mapValues([...this.data], this.rawData);

		this.valuesMapped = true;
	}

	private findEachIndexById(id: number): number {
		let index = -1;
		for (const [key, value] of Object.entries(this.$v.data.$each)) {
			if (value?.$model?.id == id) {
				index = +key;
			}
		}
		return index;
	}

	public getRawDataById(id: number): any {
		return this.rawData.find(x => x.id == id);
	}

	/**
	 * @summary Method which will set the focused component is one has been initalized in the metaData
	 * this is done on focus of a certain component
	 */
	private async componentFocused(
		data: any,
		item: { field: VueBootstrapField; index: number; item: any /** Singler data row */ }
	): Promise<void> {
		const metadata = this.metaData.find(x => x.displayProperty == item.field.key);

		if (!metadata?.focusedComponent?.component) {
			this.componentUnfocused();
			return;
		}

		this.currentRow = this.rawData.find(x => x.id == item.item.id);

		// create a local version of props so we don't override our props on our metaData
		const props = {};

		for (const [key, value] of Object.entries(metadata.focusedComponent.props)) {
			if (typeof value == "function") {
				const cb: BulkCallbackResult<any, undefined, any> = {
					currentRow: this.currentRow,
					eventData: null,
					vm: this
				};
				props[key] = await value(cb);
			} else {
				props[key] = value;
			}
		}

		this.focusedComponent = {
			component: metadata.focusedComponent.component,
			property: item.field.key,
			index: item.index,
			props: props,
			events: metadata.focusedComponent.events,
			hasVModel: metadata.focusedComponent.hasVModel,
			id: item.item.id,
            class: metadata.focusedComponent.class
		};
	}

    public async updateProp(currentProp: string, targetProp: string) {
        if (!this.focusedComponent) {
            return;
        }

        const metadata = this.metaData.find(x => x.displayProperty == this.focusedComponent.property);
        
        const cb: BulkCallbackResult<any, undefined, any> = {
            currentRow: this.currentRow,
            eventData: null,
            vm: this
        };

        this.focusedComponent.props[currentProp] = await metadata.focusedComponent.props[targetProp](cb);

        this.$forceUpdate();
    }

	/**
	 * @summary Method which will add a new row to the table
	 * method also will jump to the current page of the added row.
	 */
	private addDataRow(): void {
		const id = this.incrementIndex();

		const rawDataRow: csvTableData<any> = {
			...this.bulkType.rawDataTemplate,
			id
		};
		this.rawData.push(rawDataRow);

		const dataRow: csvTableData<any> = {
			...this.bulkType.bulkTemplate,
			id
		};

		this.data.push(dataRow);

		// Force pagination to navigate to last page.
		const result = Math.ceil(Math.max(this.rowCount, 1) / Math.max(this.pageSize, 1));
		this.currentPage = result;
	}

	/**
	 * @summary Method which will return the desired invalid tooltip message on hover
	 */
	private getInvalidTooltip(propertyName: string, def: string): string {
		return this.metaData.find(x => x.displayProperty == propertyName)?.invalidTooltip ?? def;
	}

	/**
	 * @summary Proxy event, this gets called whenever a custom event is triggered from the focused component
	 * this will allow us to execute the middleware function provided
	 */
	private async proxyEvent(eventName: string, eventData: any, loading: any): Promise<void> {
		if (eventData === undefined) return; //ignore undefined

		let option: BulkImportOption<any> | null = null;

		let val = eventData;

		if (this.focusedComponent.events[eventName]) {
			option = await this.callEventCallback(eventName, eventData, loading);

			val = option.data;

			if (option.status == BulkImportOptionStatus.Continue || option.status == BulkImportOptionStatus.Error) {
				return;
			}
		}

		const row = this.data.find(x => x.id == this.focusedComponent.id);

		this.previousValue = row[this.focusedComponent.property];
		this.newValue = val;

		row[this.focusedComponent.property] = val;

		this.reEvaluateFocusedRow(this.focusedComponent.id);

		this.previousEdited = { index: this.focusedComponent.index, property: this.focusedComponent.property };

		if (this.metaData.find(x => x.displayProperty == this.focusedComponent.property)?.resolveMultiple) {
			if (!this.data.some(x => x[this.previousEdited.property] == this.previousValue)) return;
			this.resolveMultipleModal = true;
		}

		this.componentUnfocused();
	}

	/**
	 * @summary If a column is changed, we do a re-evalulate on the row which is using the mapValues func passed by our metaData.
	 * We do this to ensure that if we have conflicting names, we remap the data again't what is described in the mapValues function.
	 *
	 * E.g. If we have 2 devices with the same name, however on different groups. Which then the group column gets edited.
	 * We want our mapValues to recalulate the deviceId as the group has been changed.
	 */
	private reEvaluateFocusedRow(id: number) {
		// If some data changes from another column,
		// we may want it to recheck the other columns in the row.
		const dataIndex = this.data.findIndex(x => x.id == id);
		const rawIndex = this.rawData.findIndex(x => x.id == id);

		const revaulatedData = this.mapValues([this.data[dataIndex]], this.rawData);
		if (revaulatedData.length > 0) {
			this.$set(this.rawData, rawIndex, revaulatedData[0]);
		}
	}

	/**
	 * @summary Method which will resolve multiple of the same validation issues
	 */
	private resolveMultiple(): void {
		// TODO: Missing functionality to push content to ValueData
		this.data = this.data.map(x => {
			return {
				...x,
				[this.previousEdited.property]:
					x[this.previousEdited.property] == this.previousValue
						? this.newValue
						: x[this.previousEdited.property]
			};
		});
		this.resolveMultipleModal = false;
	}

	/**
	 * @summary Method which handdles dynamic event callbacks
	 */
	private async callEventCallback(eventName: string, eventData: any, loading: Function): Promise<BulkImportOption<any>> {
		let val = eventData;
		const payload: BulkCallbackResult<any, any, any> = {
			currentRow: this.data.find(x => x.id == this.focusedComponent.id),
			eventData: loading ? {data: eventData, loading} : eventData,
			vm: this
		};
		const option = await this.focusedComponent.events[eventName](payload);

		if (!option) {
			return { data: [null, null], status: BulkImportOptionStatus.Error };
		}

		if (!option.data) {
			option.data = [null, null];
		}

		const [value, result] = option.data;

		if (option.status == BulkImportOptionStatus.Continue) {
			return option;
		}

		val = value;
		if (result) {
			this.setOrUpdateActualData(
				result,
				this.focusedComponent.id,
				this.metaData.find(x => x.displayProperty == this.focusedComponent.property).valueProperty
			);
		}

		// Check if we got any validation, if not ensure we aren't stuck in show invalid
		if (this.invalidCount === 0) {
			this.showInvalidRows = false;
		}

		return { data: val, status: option.status };
	}

	/**
	 * @summary Method which sets or updates the actual property to be used on upload.
	 */
	private setOrUpdateActualData(value: any, id: number, valueProperty: string): void {
		this.removeServerSideValidationAlert(id);
		this.removesubmittedIdById(id);

		const isfound = this.rawData.findIndex(x => x.id == id);
		if (isfound == -1) {
			this.rawData.push({
				...this.bulkType.rawDataTemplate,
				[valueProperty]: value
			});
		} else {
			this.rawData[isfound] = {
				...this.rawData[isfound],
				[valueProperty]: value
			};
		}
	}

	private removesubmittedIdById(id: number): void {
		const index = this.submittedIds.findIndex(x => x == id);
		this.submittedIds.splice(index, 1);
	}

	private invalidState(id: number, key: string): boolean {
		const index = this.findEachIndexById(id);
		if (index == -1) {
			return false;
		}
		return this.$v.data?.$each[index][key]?.$invalid ?? false;
	}

	/**
	 * @summary Method which will delete the selected row
	 */
	private removeRow(id: number): void {
		const index = this.data.findIndex(x => x.id == id);
		if (index >= 0) {
			this.data.splice(index, 1);
		}

		const rawIndex = this.rawData.findIndex(x => x.id == id);
		if (rawIndex >= 0) {
			this.rawData.splice(rawIndex, 1);
		}
	}

	/**
	 * @summary Method which resets the current page to default (1)
	 */
	private resetToInitalPage(): void {
		this.currentPage = 1;
	}

	/**
	 * @summary Method which will set the currently edited table row value
	 */
	private tableValue(value: any, id: number, key: string): void {
		this.removeServerSideValidationAlert(id);
		this.data.find(x => x.id == id)[key] = value;
	}

	/**
	 * @summary method which will remove server side validation
	 */
	private removeServerSideValidationAlert(id: number): void {
		if (!this.serverSideValidation[id]) return;
		delete this.serverSideValidation[id];
	}

	/**
	 * @summary Method for b-table for on filter
	 */
	private onFiltered(filteredItems): void {
		// Trigger pagination to update the number of buttons/pages due to filtering
		this.totalRows = filteredItems.length;
		this.currentPage = 1;
	}

	/**
	 * @summary event if the page is changed.
	 */
	private pageChange(): void {
		this.componentUnfocused();
	}
}
